import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContactSection from '../components/ContactSection';
import { FiCheck, FiX, FiUser, FiMail, FiPhone, FiMessageSquare, FiDollarSign, FiClock } from 'react-icons/fi';

const ServicesPage = () => {
  const [showTaxReliefForm, setShowTaxReliefForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    irsDebt: '',
    situation: '',
    contactTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const wealthManagementServices = [
    "Personal Retirement Accounts",
    "Business Retirement Plans",
    "401(k) Rollovers",
    "Wealth Management",
    "Retirement Planning",
    "College Planning",
    "Estate Planning",
    "Online Account Access"
  ];

  const taxServices = [
    "Personal Income Tax Preparation",
    "Business Income Tax Preparation",
    "Audit Protection Services",
    "Tax Planning",
    "Tax Consultations",
    "Remote, In-Person, or Drop-Off Options",
    "Bank Products via Refund Advantage"
  ];

  const additionalServices = [
    "Complimentary Notary Services for All Clients",
    "Detailed Written Financial Plan for Active Wealth Management Clients",
    "Financial Coaching Program via Ramsey Solutions",
    "Client Resource Portal Access"
  ];

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedPhone }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openTaxReliefForm = (e) => {
    e.preventDefault();
    setShowTaxReliefForm(true);
  };

  const closeTaxReliefForm = () => {
    setShowTaxReliefForm(false);
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'community-tax-contact',
          ...formData
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          irsDebt: '',
          situation: '',
          contactTime: ''
        });
        setTimeout(() => {
          closeTaxReliefForm();
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="pt-32 pb-16 bg-blue-800 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl">Comprehensive solutions designed specifically for your unique financial situation.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold mb-6">Personalized Financial Solutions</h2>
              <p className="text-lg text-gray-700">
                We understand that no two clients are the same. The goal, as experienced & knowledgeable tax & financial professionals, is to design strategies developed specifically for your own unique situation. Those strategies will help you reach your financial goals, while securing the maximum possible tax benefit.
              </p>
            </motion.div>

            <div className="space-y-16">
              {/* Wealth Management Services */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-blue-600"
              >
                <div className="flex items-center mb-6">
                  <img
                    src="https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d5563664f56aacaf657003_wealthmanagementicon.png"
                    alt="Wealth Management"
                    className="w-16 h-16 mr-4"
                  />
                  <h3 className="text-2xl font-bold">Wealth Management Services</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  Our comprehensive wealth management services are designed to help you build, grow, and preserve your wealth for the future. We focus on creating personalized strategies that align with your financial goals and risk tolerance.
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {wealthManagementServices.map((service, index) => (
                    <li key={index} className="flex items-center">
                      <FiCheck className="text-blue-600 mr-2 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Tax Services */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-green-600"
              >
                <div className="flex items-center mb-6">
                  <img
                    src="https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d55650ed82aea1b4c1ec04_icon-taxpreparation.png"
                    alt="Tax Preparation"
                    className="w-16 h-16 mr-4"
                  />
                  <h3 className="text-2xl font-bold">Tax Preparation Services</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  Our tax services are comprehensive and designed to minimize your tax burden while ensuring compliance with all applicable tax laws. We stay up-to-date on the latest tax code changes to maximize your benefits.
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {taxServices.map((service, index) => (
                    <li key={index} className="flex items-center">
                      <FiCheck className="text-green-600 mr-2 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                  <li className="flex items-center">
                    <FiCheck className="text-green-600 mr-2 flex-shrink-0" />
                    <button
                      onClick={openTaxReliefForm}
                      className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
                    >
                      Tax Relief Services
                    </button>
                  </li>
                </ul>
              </motion.div>

              {/* Additional Services */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-purple-600"
              >
                <div className="flex items-center mb-6">
                  <img
                    src="https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d55b1c3eb0f9648b48a16c_otherservices.jpg"
                    alt="Additional Services"
                    className="w-16 h-16 mr-4 object-cover rounded"
                  />
                  <h3 className="text-2xl font-bold">Additional Services</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  We offer a variety of additional services to our clients to ensure you have all the support you need to achieve financial success. These complementary services add value to your relationship with Snow's Tax & Financial.
                </p>
                <ul className="space-y-3">
                  {additionalServices.map((service, index) => (
                    <li key={index} className="flex items-center">
                      <FiCheck className="text-purple-600 mr-2 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
              <p className="text-lg text-gray-700 mb-8">
                Schedule a consultation with our team to discuss how we can help you achieve your financial goals.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://lunacal.ai/simeon-snow"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Schedule Appointment
                </a>
                <a
                  href="tel:18702084089"
                  className="bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition-colors"
                >
                  Call (870) 208-4089
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactSection />

      {/* Tax Relief Services Form Modal */}
      <AnimatePresence>
        {showTaxReliefForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeTaxReliefForm}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center">
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757642747003-community-tax-square-logo.png"
                    alt="Community Tax"
                    className="w-12 h-12 mr-4"
                  />
                  <h2 className="text-2xl font-bold text-gray-900">Tax Relief Services</h2>
                </div>
                <button onClick={closeTaxReliefForm} className="text-gray-400 hover:text-gray-600">
                  <FiX size={24} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Partnership Information */}
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">
                    We partner with one of the largest Tax Resolution Teams in the Country:
                  </h3>
                  <ul className="text-blue-800 space-y-2">
                    <li>• 4.7 star rating on The Credit Review</li>
                    <li>• Consulted with over 110k clients</li>
                    <li>• Over $1.3 billion in tax liability resolved</li>
                    <li>• Licensed Tax Practitioners, Attorneys, CPA's and IRS Enrolled Agents on Staff</li>
                    <li>• Tax Debt Settlement & Complete Tax Resolution Services</li>
                  </ul>
                </div>

                {/* Instructions */}
                <div className="mb-6">
                  <p className="text-gray-700 text-center">
                    Input your contact details below & we will reach out to you for a complimentary consultation to see if these services can help you.
                  </p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
                    <h4 className="font-semibold mb-2">Thank You!</h4>
                    <p>Your information has been submitted successfully. We will contact you soon for your complimentary consultation.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    Sorry, there was an error submitting your information. Please try again.
                  </div>
                )}

                {/* Contact Form */}
                {submitStatus !== 'success' && (
                  <form onSubmit={handleSubmit} name="community-tax-contact" data-netlify="true" method="POST">
                    <input type="hidden" name="form-name" value="community-tax-contact" />

                    {/* Name Field */}
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiUser className="inline mr-1" />
                        Name: *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="Your full name"
                      />
                    </div>

                    {/* Phone Field */}
                    <div className="mb-4">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiPhone className="inline mr-1" />
                        Phone Number: *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="(555) 123-4567"
                        maxLength="14"
                      />
                    </div>

                    {/* Email Field */}
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiMail className="inline mr-1" />
                        Email Address: *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    {/* IRS Debt Amount Field */}
                    <div className="mb-4">
                      <label htmlFor="irsDebt" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiDollarSign className="inline mr-1" />
                        Amount of IRS Debt: *
                      </label>
                      <input
                        type="text"
                        id="irsDebt"
                        name="irsDebt"
                        value={formData.irsDebt}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="e.g., $50,000 or Approximate amount"
                      />
                    </div>

                    {/* Situation Description Field */}
                    <div className="mb-4">
                      <label htmlFor="situation" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiMessageSquare className="inline mr-1" />
                        Describe your situation that you need assistance with: *
                      </label>
                      <textarea
                        id="situation"
                        name="situation"
                        value={formData.situation}
                        onChange={handleInputChange}
                        required
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors resize-none"
                        placeholder="Please describe your tax situation and what assistance you need..."
                      />
                    </div>

                    {/* Best Contact Time Field */}
                    <div className="mb-6">
                      <label htmlFor="contactTime" className="block text-sm font-medium text-gray-700 mb-2">
                        <FiClock className="inline mr-1" />
                        Best available time to contact you?: *
                      </label>
                      <input
                        type="text"
                        id="contactTime"
                        name="contactTime"
                        value={formData.contactTime}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                        placeholder="e.g., Weekdays 9am-5pm, Evenings after 6pm, etc."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ServicesPage;